import { json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { Icon } from '#app/components/media/icon.tsx'
import { Link } from '#app/components/navigation/link.tsx'
import { Code } from '#app/components/typography/code.tsx'
import { H2 } from '#app/components/typography/h2.tsx'
import { Ol } from '#app/components/typography/ol.tsx'
import { P } from '#app/components/typography/p.tsx'
import { CodeBlock } from '#app/old/components/data-display/code-block.tsx'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Usage, Styling } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Icon',
}

const component: componentProps = {
	name: 'Icon',
	shortName: 'Icon',
	fileName: 'icon',
	category: 'media',
	description: [`Renders an SVG icon with optimal performance.`],
	features: [
		'Uses SVG sprites for optimal icon performance.',
		'Comes with a script to automatically generate the SVG sprite.',
		'The icon defaults to the size of the font.',
		'Accepts a size prop to set the icon size.',
		'Accepts a label prop to add an accessible label.',
	],
	usage: `<P>
	This icon has the same size as this font: <Icon name="avatar" size="font" label="Account" className="text-emerald-500" />
</P>
<Icon name="avatar" size="sm" label="Account" className="text-emerald-500" />
<Icon name="avatar" size="md" label="Account" className="text-emerald-500" />
<Icon name="avatar" size="lg" label="Account" className="text-emerald-500" />`,
	parts: [],
	styling: `.icon {
	@apply inline;

	/* Sizes */
	&.font {
		@apply h-[1em] w-[1em];
	}
	/* Sizes */
	&.sm {
		@apply h-fluid-12 w-fluid-12;
	}
	&.md {
		@apply h-fluid-14 w-fluid-14;
	}
	&.lg {
		@apply h-fluid-15 w-fluid-15;
	}
}`,
}

export const loader = async () => {
	const source = await readSource({ category: component.category, fileName: component.fileName })
	return json({ source })
}

const HeadingRoute = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, shortName, fileName, features, usage, styling } = component

	return (
		<>
			<Description name={name} description={description} />
			<Usage usage={usage} shortName={shortName} />
			<P>
				This icon has the same size as this font: <Icon name="avatar" size="font" label="Account" className="text-emerald-500" />
			</P>
			<Icon name="avatar" size="sm" label="Account" className="text-emerald-500" />
			<Icon name="avatar" size="md" label="Account" className="text-emerald-500" />
			<Icon name="avatar" size="lg" label="Account" className="text-emerald-500" />

			<Features features={features} />
			<H2>Usage notes</H2>
			<P>
				SVG sprites are useful if you want to style them via CSS, like changing the foreground color with <Code>text-emerald-500</Code> for example. If you want to load complex SVGs, use
				standard images instead with a simple <Code>{'<img src>'}</Code>.
			</P>
			<H2>Workflow</H2>
			<P>
				Here's the workflow for adding new SVG sprites to the <Code>{'<Icon>'}</Code> component:
			</P>
			<Ol>
				<li>
					Add a new SVG icon to the <Code>./icon/svgs</Code> folder that comes with this component
				</li>
				<li>
					Run <Code>npm run build:icons</Code> to update the SVG sprite
				</li>
				<li>Use the new icon</li>
			</Ol>
			<H2>Installation</H2>
			<Ol>
				<li>
					<P>Install the icon component</P>
					<P>
						{/* eslint-disable-next-line remix-react-routes/use-link-for-routes */}
						<a href="/files/icon.zip" download="icon.zip" className="underline">
							Download
						</a>{' '}
						and unzip the file into the <Code>/app/components/media</Code> folder
					</P>
				</li>
				<li>
					Install the{' '}
					<Link to="/docs/components/layout/visually-hidden">
						<Code>{'<VisuallyHidden>'}</Code>
					</Link>{' '}
					component
				</li>
				<li>
					<P>
						Install the <Code>tailwind-merge.ts</Code> file
					</P>
					<CodeBlock
						code={`import { type ClassValue, clsx } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

const twMerge = extendTailwindMerge<string, string>({
	extend: {
		classGroups: {
			colors: [{ 'text-muted': ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'] }, 'text-ring'],
			spacing: [{ 'text-fluid': ['6xl', '5xl', '4xl', '3xl', '2xl', 'xl', 'lg', 'md', 'sm', 'xs', 'tooltip'] }],
		},
	},
})

export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs))
`}
						filename="tailwind-merge"
						extension="ts"
					/>
				</li>
				<li>
					<P>Install Prettier (if you haven't).</P>
					<P>
						Run <Code>npm i -D prettier</Code>
					</P>
					<P>
						<Code>built-icons.js</Code> uses it to format the generated files.
					</P>
				</li>
				<li>
					<P>
						Add this to your <Code>package.json</Code> scripts
					</P>
					<P>
						<Code>"build:icons": "node app/components/media/icon/build-icons.js"</Code>
					</P>
				</li>
				<li>
					<P>Check if everything is working</P>
					Run <Code>npm run build:icons</Code>
				</li>
				<li>
					<P>
						Finally make sure to preload your <Code>sprite.svg</Code> file:
					</P>
					<CodeBlock
						code={`<head>
  <link rel="preload" as="image/svg+xml" href="path/to/sprite.svg">
</head>`}
						filename="root"
						extension="tsx"
					/>
				</li>
			</Ol>

			<P>
				Depending on your server’s configuration, you might need to make sure the proper cache-headers are set on your <Code>sprite.svg</Code> so the browser can cache it appropriately.
			</P>

			<Source source={source} fileName={fileName} />
			<Styling styling={styling} />
		</>
	)
}
export default HeadingRoute
